/**
 * Pure routing predicates extracted from `router.tsx` so they're unit-testable
 * in isolation. The router is a JSX-heavy React component; the decisions
 * about WHICH layout to render for a given (venue-config, pathname, runtime)
 * are pure logic that historically lived inline and produced silent regressions
 * (Love Inn native app rendered the website mid-2026-09; see PR #184).
 *
 * Rules of thumb encoded here:
 *   - Native app ALWAYS gets app-mode. It's a native shell wrapping the SPA;
 *     rendering the website inside it is always wrong.
 *   - Browser can render the website when the venue explicitly opts in
 *     (websiteMode) or when a non-home slug matches a configured sitePage
 *     (Prospect's /account-deletion doesn't need websiteMode).
 */

import { pageForPath, type SitePagesConfig } from "./site-pages";

export type SitePagesRouteInput = {
  sitePages: SitePagesConfig | undefined | null;
  pathname: string;
  isNativeApp: boolean;
  websiteMode: boolean; // caller's already-computed websiteMode boolean (accounts for coming-soon etc.)
  /**
   * True inside the admin preview iframe: draft pages should render so
   * operators can build them before publishing. Caller passes the flag (kept
   * pure by not touching preview-overlay from here).
   */
  includeDrafts?: boolean;
};

/**
 * True when the router should hand pathname to <SitePagesSite>.
 *
 * Native app: NEVER. This is the load-bearing rule, Love Inn users were
 * seeing the website inside their app because this gate was missing.
 * Browser home ("/"): only when websiteMode is on (protects venues that
 * haven't opted their whole home over from having it hijacked by a config
 * entry with an empty slug).
 * Browser non-home: whenever the venue has that slug configured, supports
 * Prospect shipping a single /account-deletion page without switching its
 * whole home to sitePages.
 * Draft pages: hidden from public visitors (pageForPath filters), rendered
 * in the admin preview iframe when `includeDrafts` is true.
 */
export function shouldRenderSitePages(input: SitePagesRouteInput): boolean {
  if (input.isNativeApp) return false;
  if (!input.sitePages?.pages?.length) return false;
  const match = pageForPath(input.sitePages, input.pathname, { includeDrafts: input.includeDrafts });
  if (!match) return false;
  if (input.pathname === "/") return input.websiteMode;
  return true;
}

/**
 * When a browser visitor hits /terms or /privacy on a VENUE host, we default
 * to Assembly's platform-wide legal page BUT let the venue override by
 * authoring their own published site page with that slug.
 *
 * Returns true when the platform LegalPage should render, false when the
 * venue's own site page wins (the router falls through to normal venue
 * routing which resolves the sitePage).
 *
 * Tyler @ Toka 2026-09-19: created a Terms page in the builder + tapped
 * "Terms" in preview, got Assembly's legal page (with our contact email +
 * "Assembly Tickets Ltd" footer). Root cause: main.tsx short-circuited
 * `/privacy` + `/terms` to LegalPage BEFORE checking the venue's sitePages.
 *
 * Note: draft pages (published:false) DON'T qualify — Assembly's legal
 * page still shows publicly until the venue publishes their override.
 */
export function shouldRenderPlatformLegalPage(input: {
  slug: "terms" | "privacy";
  sitePages: SitePagesConfig | undefined | null;
}): boolean {
  const pages = input.sitePages?.pages;
  if (!Array.isArray(pages) || pages.length === 0) return true;
  const venueOverride = pages.find((p) => (p.slug ?? "") === input.slug && p.published !== false);
  return !venueOverride;
}

/**
 * /admin/sessions is a WELLNESS-only page (Toka's session-slot grid). Other
 * archetypes have no session slots, so the page loads an empty state that
 * looks like a broken navigation.
 *
 * PR #221 peer-review LOW: backend handlers are auth-scoped by venueId, so a
 * non-WELLNESS admin hitting this route was functionally safe (they'd see
 * zero slots), but the UX read as a bug. Return false for anything but
 * WELLNESS so the guard in the router bounces them to /admin.
 */
export function shouldRenderAdminSessions(archetype: string | null | undefined): boolean {
  return archetype === "WELLNESS";
}

/**
 * When `appConfig.comingSoon.enabled=true` the browser should ONLY ever see
 * the coming-soon splash, never the app-shell. The router historically only
 * gated `pathname === "/"`, so any deep link (`/noticeboard`, `/book`,
 * `/tickets`, `/events/<slug>`, `/sessions`, `/benefits`, `/menu`, etc.)
 * fell through to the app-shell, which is a fixed-viewport native layout
 * and looks broken in Safari (Fenton flagged 2026-09-30 on Toka mobile:
 * "when I scroll up it just loads the app rather than scroll").
 *
 * Rules encoded here:
 *   - Native app: never gate. The app IS the app-shell; hiding its own
 *     interior behind the splash would brick a launched-app venue that
 *     still has coming-soon on for browsers.
 *   - Admin paths (`/admin`, `/admin/*`): never gate so operators can
 *     manage a pre-launch venue.
 *   - Auth paths (`/sign-in`, `/sign-up`, `/forgot-password`,
 *     `/reset-password`): never gate so admins can log in.
 *   - Everything else in a browser: render the splash regardless of
 *     pathname. The URL bar keeps its path; the render is the splash.
 *     A deep-linked user gets the "not open yet" message and the
 *     mailing-list CTA, not a broken half-rendered app.
 */
export function shouldRenderComingSoon(input: {
  comingSoonEnabled: boolean;
  pathname: string;
  isNativeApp: boolean;
  /** location.search including the leading "?". Optional so old callers
   *  keep working; when omitted, transactional exemptions can't fire. */
  search?: string;
}): boolean {
  if (!input.comingSoonEnabled) return false;
  if (input.isNativeApp) return false;
  if (isAdminPathname(input.pathname)) return false;
  if (isAuthPathname(input.pathname)) return false;
  if (isTransactionalReturnUrl(input.search ?? "")) return false;
  return true;
}

/**
 * Stripe Checkout redirects the buyer back to a per-flow success URL that
 * always includes one of these query parameters (the payment id / session
 * id). If a comingSoon venue takes even one booking or ticket sale via a
 * private link, the buyer MUST land on their receipt, not the splash.
 * The signal is safe as an allowlist: none of these query params exist on
 * a regular pre-launch marketing URL.
 *
 * Sources (search for `success_url` under `src/lib/handlers/`):
 *   - tickets/checkout.ts:    ?purchased=<sid>&kind=ticket-session
 *   - tickets/order-checkout: ?purchased=<sid>&kind=festival
 *   - sessions/checkout.ts:   ?booked=<sid>&kind=session
 *   - records/checkout.ts:    ?checkout=<orderId>
 */
export function isTransactionalReturnUrl(search: string): boolean {
  if (!search) return false;
  const q = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  return params.has("purchased") || params.has("booked") || params.has("checkout");
}

function isAdminPathname(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function isAuthPathname(pathname: string): boolean {
  return (
    pathname === "/sign-in" ||
    pathname === "/sign-up" ||
    pathname === "/forgot-password" ||
    pathname.startsWith("/forgot-password/") ||
    pathname === "/reset-password" ||
    pathname.startsWith("/reset-password/")
  );
}
